import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { 
  CloudDownload, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Database,
  Users,
  FileText,
  MessageSquare
} from 'lucide-react';

const SyncFromProductionModal = ({ open, onClose, onSync }) => {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);

    try {
      const response = await onSync();
      setResult(response);
      
      // Auto-close after 5 seconds on success
      if (response.success) {
        setTimeout(() => {
          handleClose();
        }, 5000);
      }
    } catch (error) {
      setResult({
        success: false,
        message: error.message || 'Erreur lors de la synchronisation'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CloudDownload className="h-5 w-5" />
            Synchroniser depuis la Production
          </DialogTitle>
          <DialogDescription>
            Télécharge automatiquement toutes les données de pvmanager.ch et les restaure en développement
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning Alert */}
          {!result && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Attention :</strong> Cette action va remplacer toutes les données actuelles de développement 
                par celles de la production (pvmanager.ch). Cette opération est irréversible.
              </AlertDescription>
            </Alert>
          )}

          {/* Sync Button */}
          {!result && (
            <div className="flex flex-col gap-3">
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">📋 <strong>Ce qui sera synchronisé :</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Tous les clubs et leurs paramètres</li>
                  <li>Tous les membres (actifs et inactifs)</li>
                  <li>Tous les PVs (brouillons et validés)</li>
                  <li>Toutes les sessions de collecte</li>
                  <li>Toutes les propositions reçues</li>
                </ul>
              </div>

              <Button
                onClick={handleSync}
                disabled={syncing}
                className="w-full"
                size="lg"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Synchronisation en cours...
                  </>
                ) : (
                  <>
                    <CloudDownload className="h-4 w-4 mr-2" />
                    Lancer la Synchronisation
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Result Display */}
          {result && (
            <Alert variant={result.success ? "default" : "destructive"} className="border-2">
              {result.success ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {result.success ? (
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-green-700 mb-2 text-lg">
                        ✅ {result.message}
                      </p>
                      <p className="text-sm text-muted-foreground mb-3">
                        Source : <strong>{result.production_url}</strong> • 
                        Taille : <strong>{result.backup_size_mb} MB</strong>
                      </p>
                    </div>

                    {result.details && (
                      <div className="space-y-3 bg-green-50 p-4 rounded-lg border border-green-200">
                        <p className="font-semibold text-green-800 mb-3">
                          📊 Données synchronisées depuis la production :
                        </p>
                        
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 bg-white p-2 rounded border border-green-100">
                            <Database className="h-4 w-4 text-blue-600" />
                            <div>
                              <div className="font-medium">{result.details.clubs_synced}</div>
                              <div className="text-xs text-muted-foreground">Clubs</div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 bg-white p-2 rounded border border-green-100">
                            <Users className="h-4 w-4 text-purple-600" />
                            <div>
                              <div className="font-medium">{result.details.members_synced}</div>
                              <div className="text-xs text-muted-foreground">Membres</div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 bg-white p-2 rounded border border-green-100">
                            <FileText className="h-4 w-4 text-green-600" />
                            <div>
                              <div className="font-medium">{result.details.pvs_synced}</div>
                              <div className="text-xs text-muted-foreground">PV</div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 bg-white p-2 rounded border border-green-100">
                            <MessageSquare className="h-4 w-4 text-orange-600" />
                            <div>
                              <div className="font-medium">{result.details.propositions_synced}</div>
                              <div className="text-xs text-muted-foreground">Propositions</div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-green-200">
                          <p className="text-xs text-green-700">
                            <strong>Type :</strong> {result.details.sync_type}
                          </p>
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-muted-foreground italic">
                      Cette fenêtre se fermera automatiquement dans quelques secondes...
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold mb-2">❌ Échec de la synchronisation</p>
                    <p className="text-sm">{result.message}</p>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {result && (
          <div className="flex justify-end">
            <Button onClick={handleClose} variant="outline">
              Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SyncFromProductionModal;
